import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronRight, FiChevronDown, FiMic, FiVideo, FiSun, FiBook, FiStar, FiExternalLink } from 'react-icons/fi';
import VideoBubble from './VideoBubble';
import { getApiUrl } from '../../utils/apiUrl';

// Get API URL for this environment
const API_URL = getApiUrl();

// Default images URLs from the original WordPress site
const DEFAULT_IMAGES = {
  logo: 'https://2025.justasmile-events.com/wp-content/uploads/2023/04/JUST-A-MESSAGE-LOGO-SANS-FOND-VIGNETTE-V2-1-1024x1024.png',
  mainImage1: 'https://2025.justasmile-events.com/wp-content/uploads/2025/09/IMG_2393-1-683x1024.jpg',
  mainImage2: 'https://2025.justasmile-events.com/wp-content/uploads/2025/08/IMG_8336-1024x768.jpg',
  // Gallery images showing the Livre d'Or device from different angles
  gallery: [
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/IMG_8338.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/IMG_8336-1024x768.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/09/IMG_2393-1-683x1024.jpg',
    'https://2025.justasmile-events.com/wp-content/uploads/2025/08/IMG_8336.jpg',
  ],
};

// Default Hotspot data for Livre d'Or Video
const DEFAULT_HOTSPOTS = [
  {
    id: 'camera',
    label: 'Caméra 4K',
    icon: 'video',
    tooltip: 'permet un enregistrement de vidéo fluide avec une qualité 4K',
    positionLeft: '50%',
    positionTop: '10%',
  },
  {
    id: 'micro',
    label: 'Micro col de cygne',
    icon: 'mic',
    tooltip: 'micro professionnel avec réducteur de bruit, notre micro capte uniquement votre voix',
    positionLeft: '25%',
    positionTop: '40%',
  },
  {
    id: 'pupitre',
    label: 'Pupitre',
    icon: 'book',
    tooltip: 'pour accompagner vos plus beaux messages',
    positionLeft: '50%',
    positionTop: '70%',
  },
  {
    id: 'led',
    label: 'Eclairage LED',
    icon: 'sun',
    tooltip: 'permet une luminosité adéquate pour un rendu optimal',
    positionLeft: '75%',
    positionTop: '35%',
  },
];

// Default FAQ data
const DEFAULT_FAQ = [
  {
    question: 'Pourquoi louer un Livre d\'Or Vidéo chez Just a Smile Events ?',
    answer: `<p>Parce que les souvenirs méritent mieux qu'un simple stylo !</p>
<p>Avec notre Livre d'Or Vidéo, vos invités laissent des messages uniques, drôles, émouvants et spontanés, face caméra, dans un cadre élégant.</p>
<p>C'est LE souvenir vivant de votre événement, à revoir encore et encore.</p>`,
  },
  {
    question: 'Comment utiliser le Livre d\'Or Vidéo ?',
    answer: `<p>C'est simple comme un sourire :</p>
<ol>
<li>Vos invités se placent devant le micro et l'écran.</li>
<li>Ils appuient sur le bouton pour enregistrer leur message.</li>
<li>Ils laissent parler leur cœur (ou leur humour).</li>
</ol>
<p>Pas besoin de technicien ni d'application : tout est autonome et guidé !</p>`,
  },
  {
    question: 'Comment se passe l\'installation ?',
    answer: `<p>On s'occupe de tout !</p>
<p>Notre équipe installe le dispositif sur le lieu de votre événement, dans un coin calme et bien décoré.</p>
<p>Le jour J, tout est prêt, fonctionnel et esthétique.</p>
<p>Après l'événement, vous recevez une compilation vidéo de tous les messages.</p>
<p>Pour une installation en extérieur, le Livre d'Or Vidéo doit obligatoirement être installé à l'ombre ou sous un abri. Si la météo ne le permet pas, il sera automatiquement installé en intérieur.</p>`,
  },
  {
    question: 'Peut-on personnaliser l\'écran ?',
    answer: `<p>Oui ! L'interface peut être habillée aux couleurs de votre événement :</p>
<p>Nom des mariés, logo d'entreprise, date, visuels…</p>
<p>Chaque détail est pensé pour que l'expérience soit à votre image.</p>`,
  },
  {
    question: 'Est-ce adapté à tous types d\'événements ?',
    answer: `<p>Absolument !</p>
<p>Mariages, anniversaires, baby showers, soirées d'entreprise, galas, salons…</p>
<p>Le Livre d'Or Vidéo s'adapte à toutes les ambiances et tous les publics.</p>
<p>Il apporte une touche d'émotion et d'originalité à chaque occasion.</p>`,
  },
];

// Icon mapping - supports both text keys and emoji keys
const ICON_MAP = {
  video: FiVideo,
  mic: FiMic,
  sun: FiSun,
  book: FiBook,
  // Emoji mappings for database data
  '📹': FiVideo,
  '🎥': FiVideo,
  '🎤': FiMic,
  '💡': FiSun,
  '☀️': FiSun,
  '📖': FiBook,
  '🎨': FiBook,
};

export default function LivreDorPresentationPage({ pageData, productData, onNavigate, onBookingClick }) {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [hotspotsFromDB, setHotspotsFromDB] = useState(null);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleReviewsInfo, setGoogleReviewsInfo] = useState({ rating: 5.0, total: 89 });

  // Load hotspots from database
  useEffect(() => {
    const loadHotspots = async () => {
      try {
        const response = await fetch(`${API_URL}/api/hotspots-config`);
        if (response.ok) {
          const data = await response.json();
          if (data['livre-dor-video']) {
            setHotspotsFromDB(data['livre-dor-video']);
          }
        }
      } catch (error) {
        console.log('Using default hotspots');
      }
    };
    loadHotspots();
  }, []);

  // Load Google Reviews (same as homepage)
  useEffect(() => {
    const loadGoogleReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/google-reviews/`);
        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            const formattedReviews = data.reviews.map(r => ({
              name: r.author_name || r.name,
              rating: r.rating,
              text: r.text,
              date: r.relative_time_description || r.date,
              avatar: r.profile_photo_url || r.avatar
            }));
            setGoogleReviews(formattedReviews);
            setGoogleReviewsInfo({
              rating: data.average_rating || 5.0,
              total: data.total_reviews || formattedReviews.length
            });
          }
        }
      } catch (error) {
        console.log('Using fallback reviews');
      }
    };
    loadGoogleReviews();
  }, []);

  // Helper to find section by type
  const getSection = (type) => {
    return pageData?.sections?.find(s => s.type === type)?.content || null;
  };

  // Get content from pageData sections - check for actual content
  const heroSection = getSection('hero_video') || getSection('hero') || {};
  const textImageSection = getSection('text_image') || {};
  const hotspotsSection = getSection('hotspots') || {};
  const faqSection = getSection('faq') || {};
  const reviewsSection = getSection('reviews') || {};
  const gallerySection = getSection('presentation_gallery') || getSection('gallery') || {};

  // Map sections to expected content structure
  const heroContent = {
    title: heroSection.title || 'LOCATION DE LIVRE D\'OR VIDÉO',
    subtitle: heroSection.subtitle || '',
    videoUrl: heroSection.videoUrl || 'https://www.youtube.com/watch?v=wMIMdaw7tPc',
    buttonText: heroSection.buttonText || 'RÉSERVATION',
    backgroundImage: heroSection.backgroundImage || heroSection.image || '',
    overlayOpacity: heroSection.overlayOpacity || 0.6,
  };
  
  const presentationContent = {
    title: textImageSection.title || '',
    text: textImageSection.text || '',
    highlightText: textImageSection.highlightText || '',
    image: textImageSection.image || '',
    paragraphs: textImageSection.paragraphs || (textImageSection.text ? [textImageSection.text] : []),
  };
  
  const hotspotsContent = hotspotsSection || {};
  const faqContent = faqSection || {};
  const reviewsContent = reviewsSection || {};

  // Installation video URL (separate from hero background video)
  const installationVideoSection = getSection('installation_video') || {};
  const installationVideoUrl = installationVideoSection.videoUrl || heroSection.installationVideoUrl || 'https://www.youtube.com/watch?v=wMIMdaw7tPc';

  // Hotspots description paragraphs - use Constructor data with fallback
  const hotspotsParagraphs = (hotspotsContent.description || hotspotsContent.description2)
    ? [hotspotsContent.description, hotspotsContent.description2].filter(Boolean)
    : [
        "Il s'agit d'une borne vidéo élégante qui permet à vos invités de laisser des messages filmés, pleins d'émotion et de spontanéité.",
        "Chaque témoignage est enregistré en qualité 4K avec un son cristallin, pour un rendu professionnel qui immortalise les moments les plus sincères de votre événement."
      ];

  // Images
  const IMAGES = {
    logo: presentationContent.logo || DEFAULT_IMAGES.logo,
    mainImage1: hotspotsFromDB?.image || hotspotsContent.mainImage || DEFAULT_IMAGES.mainImage1,
    mainImage2: presentationContent.secondImage || DEFAULT_IMAGES.mainImage2,
    gallery: gallerySection?.images || presentationContent.gallery || DEFAULT_IMAGES.gallery,
  };

  // Extract YouTube video ID - use Constructor only (Option B)
  const getYouTubeId = (url) => {
    // Get from Constructor URL
    if (url) {
      const match = url.match(/(?:v=|\/embed\/|youtu\.be\/)([^&\?\/]+)/);
      if (match) {
        return match[1];
      }
    }
    // Default fallback (vidéo par défaut si rien configuré dans le Constructor)
    return 'wMIMdaw7tPc';
  };
  const videoId = getYouTubeId(heroContent.videoUrl);

  // Hotspots - Use DB data (x/y format) or fall back to section items or defaults
  const HOTSPOTS = (hotspotsFromDB?.hotspots || hotspotsContent.items || DEFAULT_HOTSPOTS).map(h => ({
    ...h,
    id: h.id,
    label: h.label,
    tooltip: h.tooltip || h.description || '',
    icon: ICON_MAP[h.icon] || FiVideo,
    position: { 
      left: h.positionLeft || `${h.x}%`, 
      top: h.positionTop || `${h.y}%` 
    }
  }));

  // FAQ
  const FAQ_DATA = faqContent.items || DEFAULT_FAQ;

  const faqTitle = faqContent.title || "Vous avez des questions ? Consultez notre FAQ :";
  const reviewsTitle = reviewsContent.title || "Qui de mieux que vous, pour en parler ?";

  return (
    <div className="livre-dor-presentation-page bg-black" data-testid="livre-dor-presentation-page">
      {/* Section 1: Hero with YouTube Video Background - Reduced Height */}
      <section className="relative h-[85vh] flex items-end justify-center overflow-hidden pb-24">
        {/* YouTube Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 scale-150"
            style={{ 
              pointerEvents: 'none',
              border: 'none',
              width: '100vw',
              height: '100vh',
              minWidth: '177.78vh',
              minHeight: '100vh'
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Livre d'Or Vidéo"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" preserveAspectRatio="none" className="w-full h-16">
            <path fill="#000000" d="M421.9,6.5c22.6-2.5,51.5,0.4,75.5,5.3c23.6,4.9,70.9,23.5,100.5,35.7c75.8,32.2,133.7,44.5,192.6,49.7c23.6,2.1,48.7,3.5,103.4-2.5c54.7-6,106.2-25.6,106.2-25.6V100H0V30.3c0,0,72,32.6,158.4,30.5c39.2-0.7,92.8-6.7,134-22.4c21.2-8.1,52.2-18.2,79.7-24.2C399.3,7.9,411.6,7.5,421.9,6.5z"></path>
          </svg>
        </div>

        {/* Content - Positioned near the wave */}
        <div className="relative z-10 text-center px-6 mb-8">
          {/* Logo supprimé comme demandé */}
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-wide"
          >
            {heroContent.title}
          </motion.h2>
          {heroContent.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-200 mb-8 max-w-2xl mx-auto"
            >
              {heroContent.subtitle}
            </motion.p>
          )}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            onClick={(e) => onBookingClick?.(e, 'livre-dor')}
            className="bg-[#e6d28e] hover:bg-[#d4c67a] text-black font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            data-testid="hero-reservation-btn"
          >
            {heroContent.buttonText || 'RÉSERVATION'}
          </motion.button>
        </div>
      </section>

      {/* Section 2: Présentation - Contenu du Constructor */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left: Text - Use Constructor content */}
            <div className="relative text-white">
              <div className="text-center md:text-left">
                {presentationContent.title ? (
                  <h2 
                    className="text-2xl md:text-3xl font-medium mb-4 leading-relaxed prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: presentationContent.title }}
                  />
                ) : (
                  <h2 className="text-2xl md:text-3xl font-medium mb-4 leading-relaxed">
                    La grande nouveauté 2026 :<br/>
                    <span className="text-[#e6d28e]">Le Livre d'Or Vidéo</span><br/>
                    <span className="text-lg">– by Just a Smile Events</span>
                  </h2>
                )}
                
                {presentationContent.text ? (
                  <div 
                    className="text-gray-300 space-y-4 leading-relaxed bg-white/5 p-6 rounded-xl border-l-4 border-[#e6d28e] prose prose-invert prose-p:text-gray-300 prose-strong:text-[#e6d28e] prose-li:text-gray-300 max-w-none"
                    dangerouslySetInnerHTML={{ __html: presentationContent.text }}
                  />
                ) : presentationContent.paragraphs?.length > 0 ? (
                  <div className="text-gray-300 space-y-4 leading-relaxed bg-white/5 p-6 rounded-xl border-l-4 border-[#e6d28e] [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline">
                    {presentationContent.paragraphs.map((p, i) => (
                      <div key={i} className="text-lg" dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                  </div>
                ) : (
                  <>
                    <p className="text-xl text-[#e6d28e] mb-6 italic">La nouvelle animation qui révolutionne vos événements.</p>
                    <div className="text-gray-300 space-y-4 leading-relaxed bg-white/5 p-6 rounded-xl border-l-4 border-[#e6d28e]">
                      <p className="text-lg">Les souvenirs ne s'écrivent plus… ils se racontent face caméra, avec émotion, sincérité et naturel.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right: Presentation Video (replaces gallery) */}
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="transform rotate-2 rounded-xl overflow-hidden shadow-2xl"
              >
                {/* Video Embed */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${(() => {
                      const videoUrl = installationVideoUrl || heroContent.videoUrl || '';
                      const match = videoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?\n]+)/);
                      return match ? match[1] : 'wMIMdaw7tPc';
                    })()}?rel=0&modestbranding=1`}
                    title="Présentation Livre d'Or Vidéo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2.5: Interactive Hotspots Image - Dark section */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Hotspot Image */}
            <div className="relative w-full max-w-lg mx-auto">
              <img
                src={IMAGES.mainImage1}
                alt="Livre d'Or Vidéo avec fonctionnalités"
                className="w-full rounded-lg shadow-xl"
              />
              
              {/* Hotspots - Visible on all devices - Icon only, expands on click */}
              <div className="absolute inset-0">
                {HOTSPOTS.map((hotspot) => {
                  const IconComponent = hotspot.icon;
                  const isActive = activeHotspot === hotspot.id;
                  return (
                    <div
                      key={hotspot.id}
                      className="absolute"
                      style={{ left: hotspot.position.left, top: hotspot.position.top }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{ 
                          scale: isActive ? 1 : [1, 1.08, 1],
                          boxShadow: isActive 
                            ? '0 0 20px rgba(230, 210, 142, 0.8)' 
                            : ['0 0 0px rgba(230, 210, 142, 0)', '0 0 15px rgba(230, 210, 142, 0.6)', '0 0 0px rgba(230, 210, 142, 0)']
                        }}
                        transition={{ 
                          duration: 1.5, 
                          repeat: isActive ? 0 : Infinity,
                          ease: "easeInOut"
                        }}
                        onClick={() => setActiveHotspot(isActive ? null : hotspot.id)}
                        className={`flex items-center justify-center rounded-full shadow-lg transition-all duration-300 ${
                          isActive
                            ? 'bg-[#e6d28e] text-black z-20 px-3 py-2 gap-2'
                            : 'bg-white/90 text-gray-800 hover:bg-[#e6d28e] hover:text-black w-8 h-8 sm:w-10 sm:h-10'
                        }`}
                      >
                        <IconComponent className={`flex-shrink-0 ${isActive ? 'w-4 h-4' : 'w-4 h-4 sm:w-5 sm:h-5'}`} />
                        {/* Show label only when active */}
                        <AnimatePresence>
                          {isActive && (
                            <motion.span
                              initial={{ opacity: 0, width: 0 }}
                              animate={{ opacity: 1, width: 'auto' }}
                              exit={{ opacity: 0, width: 0 }}
                              className="text-xs sm:text-sm font-medium whitespace-nowrap overflow-hidden"
                            >
                              {hotspot.label}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                      
                      {/* Tooltip with description */}
                      <AnimatePresence>
                        {isActive && hotspot.tooltip && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 bg-gray-800 text-white text-xs sm:text-sm p-2 sm:p-3 rounded-lg shadow-xl w-36 sm:w-48 text-center z-30 border border-gray-700"
                          >
                            {hotspot.tooltip}
                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-3 h-3 bg-gray-800 transform rotate-45 -mt-1.5" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
              
              {/* Mobile: Feature list below image - more prominent */}
            </div>

            {/* Right: What is Livre d'Or Vidéo */}
            <div>
              <h2 className="text-3xl md:text-4xl font-medium mb-6">
                <span className="text-white">Qu'est-ce qu'un </span>
                <span className="gold-shimmer-text">Livre d'Or Vidéo</span>
                <span className="text-white"> ?</span>
              </h2>
              <div className="text-white space-y-4 leading-relaxed text-lg [&_strong]:font-bold [&_strong]:text-[#e6d28e] [&_em]:italic [&_u]:underline">
                {hotspotsParagraphs.map((p, idx) => (
                  <div key={idx} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Un micro qui capte uniquement la voix */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
            <span className="text-white">Un micro qui capte </span>
            <span className="gold-shimmer-text">uniquement la voix</span>
            <span className="text-white"> — où que vous soyez</span>
          </h2>
          <div className="text-lg text-white/90 space-y-6 bg-gray-900 p-8 rounded-xl border-l-4 border-[#e6d28e]">
            <p>Grâce à notre micro professionnel haute précision, seule la voix de vos invités est enregistrée, même :</p>
            <ul className="list-disc list-inside space-y-2 pl-4 text-white/70">
              <li>au milieu du bruit,</li>
              <li>près du dancefloor,</li>
              <li>en extérieur,</li>
              <li>dans une salle très animée.</li>
            </ul>
            <p className="font-medium text-[#e6d28e]">Peu importe l'endroit où il est installé, le résultat reste clair, propre et parfaitement audible.</p>
          </div>
        </div>
      </section>

      {/* Section 4: Un concept élégant */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div className="bg-gray-900 p-8 rounded-xl border-l-4 border-[#e6d28e]">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Un concept élégant, simple et 100% autonome
              </h2>
              <div className="text-gray-300 space-y-4 leading-relaxed">
                <p>Installé dans n'importe quel coin de votre événement, le Livre d'Or Vidéo s'intègre naturellement à votre décoration. Vos invités peuvent venir spontanément pour laisser :</p>
                <ul className="list-disc list-inside space-y-2 pl-4 text-gray-400">
                  <li>un message personnalisé</li>
                  <li>une anecdote</li>
                  <li>un mot d'amour</li>
                  <li>une dédicace pleine d'humour</li>
                </ul>
                <p className="font-medium mt-4 text-[#e6d28e]">Chaque message est filmé avec une image soignée et un son impeccable.</p>
              </div>
            </div>
            
            {/* Right: Image */}
            <div>
              <img
                src={IMAGES.mainImage2}
                alt="Livre d'Or Vidéo en action"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Votre film souvenir final */}
      <section className="py-16 md:py-24 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            <span className="gold-shimmer-text">Votre film souvenir final</span>
          </h2>
          <div className="text-lg text-gray-300 space-y-6 bg-gray-900 p-8 rounded-xl border-l-4 border-[#e6d28e]">
            <p>Après l'événement, tous les témoignages sont montés en une seule vidéo élégante, moderne et pleine d'émotions.</p>
            <p className="text-xl text-[#e6d28e] font-medium">Un souvenir unique que vous pourrez revoir à vie et partager facilement.</p>
          </div>
        </div>
      </section>

      {/* Section 6: Adapté à tous les événements */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Adapté à tous les événements
          </h2>
          <div className="text-lg text-gray-300 space-y-6 bg-gray-900 p-8 rounded-xl border-l-4 border-[#e6d28e]">
            <p>Mariages, anniversaires, baptêmes, EVJF/EVG, soirées privées ou événements d'entreprise :</p>
            <p className="text-xl font-semibold text-[#e6d28e]">le Livre d'Or Vidéo devient l'animation coup de cœur de vos invités.</p>
          </div>
          <button
            onClick={() => onNavigate?.('livre-dor-tarifs')}
            className="mt-8 bg-[#e6d28e] hover:bg-[#d4c67a] text-black font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 inline-flex items-center gap-2"
          >
            Réserver maintenant
            <FiChevronRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Section 7: FAQ */}
      <section className="py-16 md:py-24 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-10 text-center">
            {faqTitle}
          </h2>
          
          <div className="space-y-4">
            {FAQ_DATA.map((item, index) => (
              <div
                key={index}
                className="border border-gray-700 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left bg-gray-900 hover:bg-gray-800 transition-colors"
                >
                  <span className="font-semibold text-white pr-4">{item.question}</span>
                  <FiChevronDown
                    className={`h-5 w-5 text-[#e6d28e] transition-transform flex-shrink-0 ${
                      openFaqIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaqIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div 
                        className="p-5 pt-0 text-gray-300 prose prose-sm prose-invert max-w-none [&_p]:text-gray-300 [&_li]:text-gray-300 [&_ol]:text-gray-300 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-4 [&_li]:my-1 [&_strong]:text-white [&_strong]:font-bold"
                        dangerouslySetInnerHTML={{ 
                          __html: (item.answer || '')
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>')
                            .replace(/&amp;/g, '&')
                            .replace(/&quot;/g, '"')
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews section removed - using scrolling marquee in Footer instead */}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && lightboxImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-5xl max-h-[90vh] w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowLightbox(false)}
                className="absolute -top-12 right-0 text-white/80 hover:text-white text-4xl font-light z-10"
              >
                ×
              </button>
              <img
                src={lightboxImage}
                alt="Gallery"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
